/* Minimal Booking — Aesop / Hermès Beauty restraint.
   Single centered column. No stepper bar — just a quiet count line.
   Buttons are hairline underlines. The whole page breathes. */

import { useEffect, useRef } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useBookingFlow, startingPrice, formatPrice } from "@/hooks/useBookingFlow";
import BookingTermsAcceptance from "@/components/BookingTermsAcceptance";

export default function MinimalBooking() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const f = useBookingFlow({ totalSteps: 4 });

  useEffect(() => { document.title = `Reserve · ${studioName}`; }, [studioName]);

  // Smooth-scroll the stage title into view on every step change so
  // mobile visitors don't have to scroll back up from the action bar
  // after tapping Continue. Skips the very first render so an
  // intentional landing on /booking?step=2 doesn't auto-scroll past
  // the masthead.
  const stageRef = useRef(null);
  const firstStepMount = useRef(true);
  useEffect(() => {
    if (firstStepMount.current) {
      firstStepMount.current = false;
      return;
    }
    const el = stageRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [f.step]);

  const STAGE_TITLES = {
    1: "Choose a sitting.",
    2: "When works.",
    3: "Tell us a little.",
    4: "Quietly confirm.",
  };

  function nextOrSubmit() {
    if (f.step < 4) f.setStep(f.step + 1);
    else f.submit();
  }

  return (
    <div className="bg-[color:var(--tenant-bg,#FAF9F6)] text-[color:var(--tenant-fg,#0E0D0C)] min-h-screen" data-testid="minimal-booking">
      <div className="max-w-2xl mx-auto px-6 pt-28 lg:pt-40 pb-32">
        {/* Quiet count line */}
        <div ref={stageRef} className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--tenant-fg,#0E0D0C)]/45 mb-12 text-center scroll-mt-24">
          {String(f.step).padStart(2, "0")} of 04
        </div>

        {/* Stage title */}
        <h1 className="font-light leading-[1.04] tracking-[-0.01em] text-center"
            style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)", fontSize: "clamp(2.75rem, 6vw, 5rem)" }}>
          {STAGE_TITLES[f.step]}
        </h1>

        {/* Gold hairline */}
        <div className="mx-auto mt-12 mb-14 h-px" style={{ width: "2rem", background: "var(--tenant-accent, #C9A961)" }} aria-hidden />

        {/* Step body */}
        {f.step === 1 && (
          <div className="space-y-2" data-testid="minimal-step-package">
            {f.services.map((s, i) => {
              const sel = (s.id === f.selected?.id) || (s.slug === f.selected?.slug);
              const price = startingPrice(s);
              return (
                <div key={s.id || i}>
                  <button type="button"
                    onClick={() => f.setSelectedId(s.id || s.slug)}
                    data-testid={`minimal-package-${i}`}
                    className="group w-full text-left py-5 border-b border-[color:var(--tenant-fg,#0E0D0C)]/12 hover:border-[color:var(--tenant-fg,#0E0D0C)]/35 transition-colors flex items-baseline justify-between gap-5">
                    <div className="flex-1">
                      <div className="font-serif text-2xl tracking-tight" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>
                        {s.name}
                      </div>
                      {s.description ? <div className="text-sm text-[color:var(--tenant-fg,#0E0D0C)]/55 mt-1 line-clamp-1">{s.description}</div> : null}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-serif italic text-lg" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>
                        {formatPrice(price) || "by request"}
                      </div>
                      <div className={`text-[10px] uppercase tracking-[0.32em] mt-1 ${sel ? "text-[color:var(--tenant-accent,#C9A961)]" : "text-[color:var(--tenant-fg,#0E0D0C)]/35"}`}>
                        {sel ? "Chosen" : "Choose"}
                      </div>
                    </div>
                  </button>
                  {/* Inline tier reveal — slides in directly under the picked
                      service so the choice flow stays a single eye-line, not
                      a "scroll to a footer block" hunt. Only shows if the
                      service exposes more than one tier. */}
                  {sel && s.tiers?.length > 1 && (
                    <div className="pl-1 pr-1 py-5 border-b border-[color:var(--tenant-fg,#0E0D0C)]/12 bg-[color:var(--tenant-accent-soft,rgba(201,169,97,0.08))] animate-in fade-in slide-in-from-top-2 duration-300" data-testid="minimal-tier-block">
                      <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55 mb-3 px-3" style={{ fontFamily: "var(--tenant-sans, system-ui)" }}>Pick a tier</div>
                      <div className="flex flex-wrap gap-x-6 gap-y-2 px-3">
                        {s.tiers.map((t) => {
                          const active = t.name === f.tierName;
                          return (
                            <button key={t.name} type="button" onClick={() => f.setTierName(t.name)} data-testid={`minimal-tier-${t.name}`}
                              className={`pb-1 transition-colors ${active ? "border-b text-[color:var(--tenant-fg,#0E0D0C)]" : "text-[color:var(--tenant-fg,#0E0D0C)]/55 hover:text-[color:var(--tenant-fg,#0E0D0C)]"}`}
                              style={active ? { borderColor: "var(--tenant-accent, #C9A961)" } : undefined}>
                              <span className="font-serif italic text-lg" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>{t.name}</span>
                              <span className="text-[10px] uppercase tracking-[0.32em] ml-2">{formatPrice(Number(t.price))}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {f.step === 2 && (
          <div className="space-y-8" data-testid="minimal-step-date">
            <label className="block text-center">
              <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55">Date</span>
              <input type="date" value={f.date} onChange={(e) => f.setDate(e.target.value)} data-testid="minimal-date"
                className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg,#0E0D0C)]/20 focus:border-[color:var(--tenant-fg,#0E0D0C)] py-3 mt-3 outline-none text-center text-3xl font-serif italic" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }} />
            </label>
            <label className="block text-center">
              <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55">Window</span>
              <select value={f.time} onChange={(e) => f.setTime(e.target.value)} data-testid="minimal-time"
                className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg,#0E0D0C)]/20 focus:border-[color:var(--tenant-fg,#0E0D0C)] py-3 mt-3 outline-none text-center text-3xl font-serif italic" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>
                {["09:00","10:00","11:00","13:00","14:00","15:00","16:00"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          </div>
        )}

        {f.step === 3 && (
          <div className="space-y-7" data-testid="minimal-step-details">
            {[
              { k: "name",  l: "Your name", t: "text",  ph: "Jane Doe" },
              { k: "email", l: "Email",     t: "email", ph: "jane@studio.com" },
              { k: "phone", l: "Phone (optional)", t: "tel", ph: "+1 555 0100" },
            ].map((field) => (
              <label key={field.k} className="block text-center">
                <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55">{field.l}</span>
                <input type={field.t} value={f.details[field.k]} placeholder={field.ph} data-testid={`minimal-${field.k}`}
                  onChange={(e) => f.setDetails({ ...f.details, [field.k]: e.target.value })}
                  className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg,#0E0D0C)]/20 focus:border-[color:var(--tenant-fg,#0E0D0C)] py-3 mt-3 outline-none text-center text-xl placeholder:text-[color:var(--tenant-fg,#0E0D0C)]/25" />
              </label>
            ))}
            <label className="block text-center">
              <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55">A short note</span>
              <textarea rows={4} value={f.details.notes} placeholder="A line about the moment you'd like remembered."
                onChange={(e) => f.setDetails({ ...f.details, notes: e.target.value })}
                className="block w-full bg-transparent border-0 border-b border-[color:var(--tenant-fg,#0E0D0C)]/20 focus:border-[color:var(--tenant-fg,#0E0D0C)] py-3 mt-3 outline-none text-center text-lg placeholder:text-[color:var(--tenant-fg,#0E0D0C)]/25 resize-none italic font-serif" data-testid="minimal-notes" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }} />
            </label>
          </div>
        )}

        {f.step === 4 && (
          <div className="text-center space-y-3" data-testid="minimal-step-review">
            {[
              ["Sitting", f.selected?.name],
              ["Tier",    f.selectedTier?.name],
              ["Date",    f.date || "—"],
              ["Time",    f.time],
              ["Guest",   f.details.name || "—"],
              ["Total",   formatPrice(Number(f.selectedTier?.price)) || "—"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-6 border-b border-[color:var(--tenant-fg,#0E0D0C)]/10 pb-3">
                <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55">{k}</span>
                <span className="font-serif italic text-xl text-[color:var(--tenant-fg,#0E0D0C)]" style={{ fontFamily: "var(--tenant-serif, 'Cormorant Garamond', serif)" }}>{v}</span>
              </div>
            ))}
            <div className="pt-6 text-left">
              <BookingTermsAcceptance
                policyText={f.policyText}
                checked={f.termsAccepted}
                onChange={f.setTermsAccepted}
                testidPrefix="minimal-booking-terms"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-16 flex items-center justify-between gap-4">
          <button type="button" onClick={f.goBack} disabled={f.step === 1} data-testid="minimal-booking-back"
            className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/50 hover:text-[color:var(--tenant-fg,#0E0D0C)] disabled:opacity-30 transition-colors">
            ← Back
          </button>
          <button type="button" onClick={nextOrSubmit} disabled={!f.canForward || f.submitting} data-testid="minimal-booking-next"
            className="pb-1 border-b text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-fg,#0E0D0C)] disabled:opacity-30 transition-opacity hover:opacity-80"
            style={{ borderColor: "var(--tenant-accent, #C9A961)" }}>
            {f.submitting ? "Sending…" : f.step < 4 ? "Continue" : "Reserve"}
          </button>
        </div>
      </div>
    </div>
  );
}
